import React, { useState, useEffect } from 'react';
import { FormModel } from '../../models/form.model';
import IconButton from '@mui/material/IconButton';
import { ChevronDownIcon, ChevronUpIcon, Edit2Icon, MoveRightIcon, Share2Icon, Trash2Icon } from 'lucide-react';
import { Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useAuthenticatedFetch } from '../../utils/auth-fetch';
import ConfirmationDialog from '../../confirmation-dialog/confirmation-dialog';

function Forms() {
    const [formsData, setFormsData] = useState<FormModel[]>([]);
    const [confirmDeleteFormDialogOpen, setConfirmDeleteFormDialogOpen] = useState(false);
    const [formIdForDeletion, setFormIdForDeletion] = useState<string>('');
    const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());

    const navigate = useNavigate();

    const { showSnackbar } = useSnackbar();
    const authenticatedFetch = useAuthenticatedFetch();

    useEffect(() => {
        getForms();
    }, []);

    const handleConfirm = async (confirmed: boolean) => {
        if (formIdForDeletion === "") {
            return;
        }
        if (confirmed) {
            await handleDelete(formIdForDeletion);
            setFormIdForDeletion("");
        }
    };

    const toggleDescription = (id: string) => {
        setExpandedDescriptions(prev => {
          const newSet = new Set(prev);
          if (newSet.has(id)) {
            newSet.delete(id);
          } else {
            newSet.add(id);
          }
          return newSet;
        });
      };

    function getForms() {
        authenticatedFetch("/api/forms")
            .then(res => res.json())
            .then(data => {
                setFormsData(data["message"] === undefined ? data : []);
            })
            .catch(error => console.error("Error fetching forms:", error));
    }

    const handleShare = async (formId: string) => {
        try {
            const response = await authenticatedFetch(`/api/forms/${formId}/share`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            if (!response.ok) throw new Error('Failed to generate share URL');
            const { shareUrl } = await response.json();
            const fullUrl = `${window.location.origin}${shareUrl}`;
            setTimeout(() => {
                navigator.clipboard.writeText(fullUrl)
              }, 0)
            showSnackbar('Share URL copied to clipboard!', 'info');
        } catch (error) {
            showSnackbar('Error generating share URL', 'error');
        }
    };

    const handleDelete = async (formId: string) => {
        try {
            const response = await authenticatedFetch(`/api/forms/${formId}`, {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error('Failed to delete form');
            getForms();
            showSnackbar('Form deleted successfully!', 'success');
        } catch (error) {
            showSnackbar('Error deleting form', 'error');
        }
    };

    return (
        <div className="overflow-x-auto w-full">
          <button
            type="button"
            className="w-36 bg-orange-600 text-white py-2 m-2 px-4 rounded hover:bg-orange-700 float-right"
            onClick={() => navigate('/forms/builder')}
          >
            + Create
          </button>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated At
                </th>
                <th scope="col" className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {formsData.map(form => (
                <tr key={form._id}>
                  <td className="px-6 py-4 whitespace-nowrap">{form._id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{form.title}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <div className={`${expandedDescriptions.has(form._id) ? '' : 'max-w-xs truncate'}`}>
                        {form.description}
                      </div>
                      <IconButton
                        onClick={() => toggleDescription(form._id)}
                        className="h-6 w-6 hover:bg-gray-100 rounded"
                      >
                        {expandedDescriptions.has(form._id) ? (
                          <ChevronUpIcon className="h-8 w-8" />
                        ) : (
                          <ChevronDownIcon className="h-8 w-8" />
                        )}
                      </IconButton>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{new Date(form.createdAt).toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{new Date(form.updatedAt).toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <Tooltip title="Open">
                      <IconButton onClick={() => navigate(`/forms/${form.shareId}`)} aria-label="share">
                        <MoveRightIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share">
                      <IconButton onClick={() => handleShare(form._id)} aria-label="share">
                        <Share2Icon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => navigate("/forms/builder", { state: form })} aria-label="edit">
                        <Edit2Icon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => { setConfirmDeleteFormDialogOpen(true); setFormIdForDeletion(form._id) }} aria-label="delete">
                        <Trash2Icon />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <ConfirmationDialog
            open={confirmDeleteFormDialogOpen}
            onClose={() => { setConfirmDeleteFormDialogOpen(false); setFormIdForDeletion("") }}
            title="Are you sure?"
            description="This action cannot be undone."
            onConfirm={(event: boolean) => handleConfirm(event)}
          />
        </div>
      );
}

export default Forms;